import { AlertTriangle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: number;
  tone: ToastTone;
  title: string;
  message?: string;
}

export interface ToastProps extends ToastMessage {
  onDismiss: (id: number) => void;
}

const TONE_CLASSES: Record<ToastTone, string> = {
  success: 'border-state-success/40 bg-surface text-ink',
  error: 'border-state-danger/40 bg-surface text-ink',
  info: 'border-state-info/40 bg-surface text-ink',
  warning: 'border-state-warning/40 bg-surface text-ink',
};

const TONE_ICON_CLASSES: Record<ToastTone, string> = {
  success: 'text-state-success',
  error: 'text-state-danger',
  info: 'text-state-info',
  warning: 'text-state-warning',
};

function ToneIcon({ tone }: { tone: ToastTone }): ReactNode {
  const className = `h-5 w-5 shrink-0 ${TONE_ICON_CLASSES[tone]}`;
  switch (tone) {
    case 'success':
      return <CheckCircle2 aria-hidden="true" className={className} />;
    case 'error':
      return <TriangleAlert aria-hidden="true" className={className} />;
    case 'warning':
      return <AlertTriangle aria-hidden="true" className={className} />;
    case 'info':
      return <Info aria-hidden="true" className={className} />;
    default:
      return null;
  }
}

/**
 * A single toast. Error/warning use role="alert" (assertive), success/info use
 * role="status" (polite). The tone is always accompanied by text — never
 * colour alone.
 */
export function Toast({ id, tone, title, message, onDismiss }: ToastProps) {
  const role = tone === 'error' || tone === 'warning' ? 'alert' : 'status';
  return (
    <div
      role={role}
      className={`toast-enter pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border p-4 shadow-pop ${TONE_CLASSES[tone]}`}
    >
      <ToneIcon tone={tone} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{title}</p>
        {message && <p className="mt-0.5 text-sm text-ink-muted">{message}</p>}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss notification"
        className="rounded-md p-1 text-ink-muted hover:bg-steel-light hover:text-ink"
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}
